import React, { useState } from 'react';
import { IconEye, IconEyeOff, IconCheck } from './icons';

/**
 * Shared UI primitives — every visual element in the widget is built from
 * these. They all read colors/radii from CSS variables emitted by the theme
 * system (see theme.ts), so restyling the widget is a matter of changing
 * the theme, not editing each component.
 */

// ---------- Spinner ----------

export const Spinner: React.FC<{ size?: number; color?: string }> = ({
  size = 20,
  color,
}) => (
  <span
    role="status"
    aria-label="Loading"
    style={{
      width: size,
      height: size,
      border: `2px solid var(--pc-color-border)`,
      borderTopColor: color || 'var(--pc-color-primary)',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'pc-spin 0.8s linear infinite',
    }}
  />
);

// ---------- Button ----------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export const Button: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ButtonVariant;
    fullWidth?: boolean;
    loading?: boolean;
    iconLeft?: React.ReactNode;
    iconRight?: React.ReactNode;
  }
> = ({
  variant = 'primary',
  fullWidth = false,
  loading = false,
  iconLeft,
  iconRight,
  children,
  disabled,
  style,
  ...rest
}) => {
  const styles = BUTTON_STYLES[variant];
  const isDisabled = disabled || loading;
  return (
    <button
      {...rest}
      disabled={isDisabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: fullWidth ? '100%' : undefined,
        padding: '12px 18px',
        fontSize: 15,
        fontWeight: 600,
        fontFamily: 'inherit',
        lineHeight: 1.2,
        borderRadius: 'var(--pc-radius-md)',
        border: styles.border,
        backgroundColor: styles.bg,
        color: styles.fg,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled && !loading ? 0.5 : 1,
        transition: 'background-color 0.15s, border-color 0.15s, transform 0.08s',
        ...style,
      }}
      onMouseOver={(e) => {
        if (!isDisabled && styles.hoverBg) e.currentTarget.style.backgroundColor = styles.hoverBg;
        if (!isDisabled && styles.hoverBorder) e.currentTarget.style.border = styles.hoverBorder;
      }}
      onMouseOut={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.backgroundColor = styles.bg;
          e.currentTarget.style.border = styles.border;
        }
      }}
      onMouseDown={(e) => {
        if (!isDisabled) e.currentTarget.style.transform = 'translateY(1px)';
      }}
      onMouseUp={(e) => {
        if (!isDisabled) e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {loading ? <Spinner size={16} color={styles.fg} /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
};

const BUTTON_STYLES: Record<
  ButtonVariant,
  { bg: string; fg: string; border: string; hoverBg?: string; hoverBorder?: string }
> = {
  primary: {
    bg: 'var(--pc-color-primary)',
    fg: 'var(--pc-color-primary-foreground)',
    border: '1px solid var(--pc-color-primary)',
    hoverBg: 'var(--pc-color-primary-hover)',
    hoverBorder: '1px solid var(--pc-color-primary-hover)',
  },
  secondary: {
    bg: 'var(--pc-color-surface)',
    fg: 'var(--pc-color-text)',
    border: '1.5px solid var(--pc-color-border)',
    hoverBg: 'var(--pc-color-surface-subtle)',
    hoverBorder: '1.5px solid var(--pc-color-border-strong)',
  },
  ghost: {
    bg: 'transparent',
    fg: 'var(--pc-color-text-muted)',
    border: '1px solid transparent',
    hoverBg: 'var(--pc-color-surface-subtle)',
  },
  danger: {
    bg: 'var(--pc-color-error-bg)',
    fg: 'var(--pc-color-error)',
    border: '1px solid var(--pc-color-error-border)',
    hoverBg: 'var(--pc-color-error-bg)',
  },
};

// ---------- TextInput ----------

export const TextInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & {
    invalid?: boolean;
    rightAction?: React.ReactNode;
  }
>(function TextInput({ invalid, rightAction, style, ...rest }, ref) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        border: `1.5px solid ${
          invalid
            ? 'var(--pc-color-error-border)'
            : focused
            ? 'var(--pc-color-primary)'
            : 'var(--pc-color-border)'
        }`,
        borderRadius: 'var(--pc-radius-md)',
        backgroundColor: 'var(--pc-color-surface)',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: focused ? `0 0 0 3px var(--pc-color-primary-soft)` : 'none',
      }}
    >
      <input
        ref={ref}
        {...rest}
        onFocus={(e) => {
          setFocused(true);
          rest.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          rest.onBlur?.(e);
        }}
        style={{
          flex: 1,
          minWidth: 0,
          padding: '12px 14px',
          fontSize: 15,
          fontFamily: 'inherit',
          color: 'var(--pc-color-text)',
          backgroundColor: 'transparent',
          border: 'none',
          outline: 'none',
          borderRadius: 'var(--pc-radius-md)',
          ...style,
        }}
      />
      {rightAction}
    </div>
  );
});

// ---------- PasswordInput with show/hide ----------

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function PasswordInput(props, ref) {
  const [visible, setVisible] = useState(false);
  return (
    <TextInput
      ref={ref}
      {...props}
      type={visible ? 'text' : 'password'}
      rightAction={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 12px',
            background: 'transparent',
            border: 'none',
            color: 'var(--pc-color-text-muted)',
            cursor: 'pointer',
            borderTopRightRadius: 'var(--pc-radius-md)',
            borderBottomRightRadius: 'var(--pc-radius-md)',
          }}
        >
          {visible ? <IconEyeOff size={18} /> : <IconEye size={18} />}
        </button>
      }
    />
  );
});

// ---------- OptionCard — selectable full-width tile ----------

export const OptionCard: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    title: string;
    description?: string;
    icon?: React.ReactNode;
    accent?: 'primary' | 'neutral';
    selected?: boolean;
    tag?: string;
  }
> = ({ title, description, icon, accent = 'neutral', selected, tag, style, ...rest }) => {
  const [hover, setHover] = useState(false);
  const isPrimary = accent === 'primary';
  return (
    <button
      {...rest}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        padding: '16px 18px',
        textAlign: 'left',
        fontFamily: 'inherit',
        fontSize: 15,
        borderRadius: 'var(--pc-radius-lg)',
        border: `1.5px solid ${
          selected
            ? 'var(--pc-color-primary)'
            : hover
            ? isPrimary
              ? 'var(--pc-color-primary-hover)'
              : 'var(--pc-color-primary)'
            : isPrimary
            ? 'var(--pc-color-primary)'
            : 'var(--pc-color-border)'
        }`,
        backgroundColor: isPrimary
          ? hover
            ? 'var(--pc-color-primary-hover)'
            : 'var(--pc-color-primary)'
          : hover
          ? 'var(--pc-color-primary-soft)'
          : 'var(--pc-color-surface)',
        color: isPrimary ? 'var(--pc-color-primary-foreground)' : 'var(--pc-color-text)',
        cursor: 'pointer',
        transition: 'background-color 0.15s, border-color 0.15s',
        ...style,
      }}
    >
      {icon && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 'var(--pc-radius-md)',
            backgroundColor: isPrimary
              ? 'rgba(255,255,255,0.16)'
              : 'var(--pc-color-primary-soft)',
            color: isPrimary
              ? 'var(--pc-color-primary-foreground)'
              : 'var(--pc-color-primary)',
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{title}</span>
          {tag && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                padding: '2px 6px',
                borderRadius: 'var(--pc-radius-sm)',
                backgroundColor: isPrimary
                  ? 'rgba(255,255,255,0.22)'
                  : 'var(--pc-color-surface-muted)',
                color: isPrimary ? 'inherit' : 'var(--pc-color-text-muted)',
              }}
            >
              {tag}
            </span>
          )}
        </span>
        {description && (
          <span
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 400,
              marginTop: 3,
              color: isPrimary
                ? 'rgba(255,255,255,0.82)'
                : 'var(--pc-color-text-muted)',
            }}
          >
            {description}
          </span>
        )}
      </span>
    </button>
  );
};

// ---------- StatusBanner — inline alert/info strip ----------

export const StatusBanner: React.FC<{
  tone?: 'info' | 'success' | 'warning' | 'error';
  icon?: React.ReactNode;
  children: React.ReactNode;
  role?: string;
}> = ({ tone = 'info', icon, children, role }) => {
  const palette = BANNER_TONES[tone];
  return (
    <div
      role={role || (tone === 'error' || tone === 'warning' ? 'alert' : undefined)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        borderRadius: 'var(--pc-radius-md)',
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {icon && <span style={{ color: palette.fg, display: 'inline-flex' }}>{icon}</span>}
      <span style={{ flex: 1 }}>{children}</span>
    </div>
  );
};

const BANNER_TONES = {
  info: {
    bg: 'var(--pc-color-primary-soft)',
    fg: 'var(--pc-color-primary)',
    border: 'var(--pc-color-primary-soft-border)',
  },
  success: {
    bg: 'var(--pc-color-success-bg)',
    fg: 'var(--pc-color-success)',
    border: 'var(--pc-color-success-border)',
  },
  warning: {
    bg: 'var(--pc-color-warning-bg)',
    fg: 'var(--pc-color-warning)',
    border: 'var(--pc-color-warning-border)',
  },
  error: {
    bg: 'var(--pc-color-error-bg)',
    fg: 'var(--pc-color-error)',
    border: 'var(--pc-color-error-border)',
  },
};

// ---------- ScreenHeader — screen-level h2 + description ----------

export const ScreenHeader: React.FC<{
  icon?: React.ReactNode;
  title: string;
  description?: string;
}> = ({ icon, title, description }) => (
  <div style={{ textAlign: 'center', marginBottom: 20 }}>
    {icon && (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 'var(--pc-radius-pill)',
          backgroundColor: 'var(--pc-color-primary-soft)',
          color: 'var(--pc-color-primary)',
          marginBottom: 12,
        }}
      >
        {icon}
      </span>
    )}
    <h3
      style={{
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--pc-color-text)',
        margin: '0 0 6px',
      }}
    >
      {title}
    </h3>
    {description && (
      <p
        style={{
          fontSize: 13,
          color: 'var(--pc-color-text-muted)',
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        {description}
      </p>
    )}
  </div>
);

// ---------- Tabs — simple segmented control ----------

export const Tabs: React.FC<{
  tabs: { id: string; label: string; icon?: React.ReactNode }[];
  activeId: string;
  onChange: (id: string) => void;
}> = ({ tabs, activeId, onChange }) => (
  <div
    role="tablist"
    style={{
      display: 'inline-flex',
      width: '100%',
      padding: 4,
      backgroundColor: 'var(--pc-color-surface-subtle)',
      borderRadius: 'var(--pc-radius-md)',
      border: '1px solid var(--pc-color-border)',
      marginBottom: 18,
      gap: 4,
    }}
  >
    {tabs.map((t) => {
      const active = t.id === activeId;
      return (
        <button
          key={t.id}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(t.id)}
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '8px 12px',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            border: 'none',
            borderRadius: 'var(--pc-radius-sm)',
            backgroundColor: active ? 'var(--pc-color-surface)' : 'transparent',
            color: active ? 'var(--pc-color-text)' : 'var(--pc-color-text-muted)',
            boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            cursor: active ? 'default' : 'pointer',
            transition: 'all 0.15s',
          }}
        >
          {t.icon}
          {t.label}
        </button>
      );
    })}
  </div>
);

// ---------- Pill — small status/type indicator ----------

export const Pill: React.FC<{
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'error';
  children: React.ReactNode;
}> = ({ tone = 'neutral', children }) => {
  const palette = PILL_TONES[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 'var(--pc-radius-sm)',
        backgroundColor: palette.bg,
        color: palette.fg,
        letterSpacing: 0.2,
      }}
    >
      {children}
    </span>
  );
};

const PILL_TONES = {
  neutral: { bg: 'var(--pc-color-surface-muted)', fg: 'var(--pc-color-text-muted)' },
  primary: { bg: 'var(--pc-color-primary-soft)', fg: 'var(--pc-color-primary)' },
  success: { bg: 'var(--pc-color-success-bg)', fg: 'var(--pc-color-success)' },
  warning: { bg: 'var(--pc-color-warning-bg)', fg: 'var(--pc-color-warning)' },
  error: { bg: 'var(--pc-color-error-bg)', fg: 'var(--pc-color-error)' },
};

// ---------- LinkButton — unstyled text button ----------

export const LinkButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = (
  props,
) => (
  <button
    {...props}
    style={{
      background: 'none',
      border: 'none',
      padding: 0,
      color: 'var(--pc-color-primary)',
      fontSize: 13,
      fontWeight: 500,
      fontFamily: 'inherit',
      cursor: 'pointer',
      textDecoration: 'underline',
      ...props.style,
    }}
  />
);

// ---------- SuccessTick — small animated check ----------

export const SuccessTick: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: 'var(--pc-color-success-bg)',
      color: 'var(--pc-color-success)',
    }}
  >
    <IconCheck size={Math.round(size * 0.55)} strokeWidth={2.5} />
  </span>
);

// ---------- Global styles (keyframes + focus) ----------

export const GlobalWidgetStyles: React.FC = () => (
  <style>{`
    @keyframes pc-spin { to { transform: rotate(360deg); } }
    @keyframes pc-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
    @keyframes pc-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
    .pc-fade-in { animation: pc-fade-in 0.18s ease-out; }
    .pc-widget-root *:focus-visible {
      outline: 2px solid var(--pc-color-primary);
      outline-offset: 2px;
    }
    .pc-widget-root ::placeholder { color: var(--pc-color-text-faded); opacity: 1; }
  `}</style>
);
